// [PURPOSE]: Batch 8 / P1 D-13 — pure helper that computes the diff when
//   a character switches races. Pre-fix, useRaceEffects (and the UI's
//   handleRaceChange) only ADDED the new race's contributions, never
//   removed the previous race's. A Dwarf → Tiefling switch left Dwarven
//   Resilience, Dwarven weapon proficiencies, Dwarvish language, etc.
//
// [DESIGN]:
//   - applyRaceTransition(character, prevRaceData, newRaceData)
//   - Returns a partial-character DELTA (suitable for `setCharacter(prev =>
//     ({ ...prev, ...delta }))`) — does NOT mutate.
//   - For each contribution category (languages, features, proficiencies,
//     damageResistance, racialSpells), we:
//       1. Subtract anything from prevRaceData that's currently in character
//          (defense in depth — only remove items that came from the prev race
//          and are still present)
//       2. Add anything from newRaceData (deduped via Set)
//   - Class/feat-derived items survive because they were never in
//     prevRaceData. The Dwarven Resilience that came from Dwarf is in
//     prevRaceData.features; the Action Surge from Fighter class is NOT,
//     so it survives the filter.
//
// [INTEGRATION]: Called by `src/hooks/useRaceEffects.js` and
//   `src/components/CharacterBasicsPanel.js` handleRaceChange.

const FEATURE_NAME = (f) => (typeof f === 'string' ? f : f && f.name);

/**
 * Subtract `removeList` from `currentList` (case-sensitive string compare).
 * Returns a new array; does not mutate.
 */
function subtract(currentList, removeList) {
  if (!Array.isArray(currentList)) return [];
  if (!Array.isArray(removeList) || removeList.length === 0) return [...currentList];
  const remove = new Set(removeList);
  return currentList.filter((x) => !remove.has(x));
}

/**
 * Subtract feature objects/strings from `currentList` whose name appears
 * in `removeNames`. Race-derived features have `source: <raceName>` but
 * we conservatively match on name alone — a class feature would never
 * collide with a race feature by name in canon SRD content.
 */
function subtractFeaturesByName(currentList, removeNames) {
  if (!Array.isArray(currentList)) return [];
  if (!Array.isArray(removeNames) || removeNames.length === 0) return [...currentList];
  const remove = new Set(removeNames);
  return currentList.filter((f) => !remove.has(FEATURE_NAME(f)));
}

/**
 * Merge two arrays preserving order, deduped (case-sensitive).
 */
function mergeUnique(a, b) {
  const out = Array.isArray(a) ? [...a] : [];
  const seen = new Set(out);
  (Array.isArray(b) ? b : []).forEach((x) => {
    if (!seen.has(x)) {
      out.push(x);
      seen.add(x);
    }
  });
  return out;
}

/**
 * Compute the delta to apply to a character when transitioning between
 * races. Returns an object spread-able onto the character — does NOT
 * mutate inputs.
 *
 * @param {Object} character
 * @param {Object|null} prevRaceData  - Previous race's data object, or null on first assignment.
 * @param {Object|null} newRaceData   - New race's data object, or null to fully tear down.
 * @returns {Object} partial character delta
 */
export function applyRaceTransition(character, prevRaceData, newRaceData) {
  const c = character || {};

  // ----- Step 1: Subtract previous race's contributions -----
  let languages = subtract(c.languages || [], prevRaceData?.languages || []);
  let damageResistance = subtract(c.damageResistance || [], prevRaceData?.damageResistance || []);
  let damageImmunity = subtract(c.damageImmunity || [], prevRaceData?.damageImmunity || []);
  let conditionImmunity = subtract(c.conditionImmunity || [], prevRaceData?.conditionImmunity || []);
  let racialSpells = subtract(c.racialSpells || [], prevRaceData?.grantedSpells || []);
  // Features may be stored as strings or {name, source}; match by name.
  const prevFeatureNames = (prevRaceData?.features || []).map(FEATURE_NAME).filter(Boolean);
  let features = subtractFeaturesByName(c.features || [], prevFeatureNames);

  // Proficiencies: per-category subtract.
  const prevProf = prevRaceData?.proficiencies || {};
  const currentProf = c.proficiencies || {};
  const proficiencies = { ...currentProf };
  Object.keys(prevProf).forEach((category) => {
    const removeList = prevProf[category];
    proficiencies[category] = subtract(currentProf[category] || [], Array.isArray(removeList) ? removeList : []);
  });

  // ----- Step 2: Apply new race's contributions -----
  if (newRaceData) {
    languages = mergeUnique(languages, newRaceData.languages || []);
    damageResistance = mergeUnique(damageResistance, newRaceData.damageResistance || []);
    damageImmunity = mergeUnique(damageImmunity, newRaceData.damageImmunity || []);
    conditionImmunity = mergeUnique(conditionImmunity, newRaceData.conditionImmunity || []);
    racialSpells = mergeUnique(racialSpells, newRaceData.grantedSpells || []);

    // Features — add as {name, source} objects so future tear-downs can match.
    const newFeatureObjects = (newRaceData.features || [])
      .map((f) => {
        const name = FEATURE_NAME(f);
        if (!name) return null;
        return { name, source: newRaceData._raceName || 'race' };
      })
      .filter(Boolean);
    const seenNames = new Set(features.map(FEATURE_NAME));
    newFeatureObjects.forEach((nf) => {
      if (!seenNames.has(nf.name)) {
        features.push(nf);
        seenNames.add(nf.name);
      }
    });

    // Proficiencies merge per-category.
    const newProf = newRaceData.proficiencies || {};
    Object.keys(newProf).forEach((category) => {
      const newList = newProf[category];
      if (!Array.isArray(newList)) return;
      proficiencies[category] = mergeUnique(proficiencies[category] || [], newList);
    });
  }

  // ----- Step 3: Static fields (size/speed/darkvision) come from new race directly -----
  const speed = newRaceData?.speed ?? c.speed ?? 30;
  const size = newRaceData?.size ?? c.size ?? 'Medium';
  const darkvision = newRaceData?.darkvision ?? c.darkvision ?? 0;

  return {
    speed,
    size,
    darkvision,
    languages,
    damageResistance,
    damageImmunity,
    conditionImmunity,
    racialSpells,
    features,
    proficiencies,
  };
}

export default applyRaceTransition;
